/*
 * Themes for HMI projects (docs/hmi-panel-ausbau.md §3).
 *
 * A theme is applied as CSS custom properties on the element that frames a
 * project (live grid, free canvas, editor, standalone runtime). It maps onto
 * the app's existing tokens (--surface-*, --text-*, --accent, --status-*,
 * --hmi-value, --radius-*, --font-sans), so every widget that already uses
 * tokens follows the theme without knowing about it.
 *
 * Pure module (no runtime imports): the tests load it directly.
 *
 * Colours come from the project body. The server accepts only #rrggbb; this
 * module checks again, because a value here ends up inside a style
 * attribute and the standalone runtime may load a body the server never saw
 * (cached on a gateway, edited by hand).
 */

export type ThemeMode = 'auto' | 'light' | 'dark';
export type ThemeDensity = 'comfortable' | 'compact' | 'touch';

export interface ThemeColors {
  background: string;
  surface: string;
  surfaceRaised: string;
  text: string;
  textMuted: string;
  border: string;
  accent: string;
  accentText: string;
  ok: string;
  warn: string;
  alarm: string;
  value: string;
}

export interface HmiTheme {
  preset?: string;
  mode?: ThemeMode;
  colors?: Partial<ThemeColors>;
  font?: string;
  radius?: number;
  density?: ThemeDensity;
}

export interface ThemePreset {
  id: string;
  label: string;
  hint: string;
  /** Mode the preset is designed for; 'auto' presets have both. */
  defaultMode: ThemeMode;
  light: ThemeColors;
  dark: ThemeColors;
  font: string;
  radius: number;
}

export interface FontOption {
  id: string;
  label: string;
  /** CSS font-family stack. The faces are bundled (offline on a panel). */
  stack: string;
}

export const FONT_OPTIONS: readonly FontOption[] = [
  { id: 'system', label: 'System', stack: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" },
  { id: 'inter', label: 'Inter', stack: "'Inter Variable', 'Inter', system-ui, sans-serif" },
  { id: 'jost', label: 'Jost', stack: "'Jost Variable', 'Jost', system-ui, sans-serif" },
  { id: 'roboto-condensed', label: 'Roboto Condensed', stack: "'Roboto Condensed Variable', 'Roboto Condensed', 'Arial Narrow', sans-serif" },
];

const LIGHT_BASE: ThemeColors = {
  background: '#f4f5f7', surface: '#ffffff', surfaceRaised: '#ffffff', text: '#16191d',
  textMuted: '#5a616b', border: '#dfe2e7', accent: '#2b7de9', accentText: '#ffffff',
  ok: '#1f9d55', warn: '#b7791f', alarm: '#d64545', value: '#16191d',
};

const DARK_BASE: ThemeColors = {
  background: '#0f1115', surface: '#171a1f', surfaceRaised: '#1f232a', text: '#f2f4f7',
  textMuted: '#9aa1ad', border: '#2a2f37', accent: '#5aa5ff', accentText: '#0b1220',
  ok: '#3fb950', warn: '#d29922', alarm: '#f85149', value: '#f2f4f7',
};

export const THEME_PRESETS: readonly ThemePreset[] = [
  {
    id: 'hvacloud', label: 'HVACloud', hint: 'Die Farben der App', defaultMode: 'auto',
    light: LIGHT_BASE, dark: DARK_BASE, font: 'inter', radius: 12,
  },
  {
    id: 'austria-email', label: 'Austria Email', hint: 'Dunkel mit Rot, wie die TP8-Demo', defaultMode: 'dark',
    light: { ...LIGHT_BASE, accent: '#c62127', accentText: '#ffffff', value: '#16191d' },
    dark: {
      background: '#101114', surface: '#1b1d22', surfaceRaised: '#24272d', text: '#f5f6f8',
      textMuted: '#9aa0aa', border: '#2c2f36', accent: '#e0393f', accentText: '#ffffff',
      ok: '#3fb950', warn: '#e3a33b', alarm: '#ff5a52', value: '#ffffff',
    },
    font: 'inter', radius: 18,
  },
  {
    id: 'sinnograte', label: 'sinnograte', hint: 'Limette auf Anthrazit', defaultMode: 'dark',
    light: { ...LIGHT_BASE, accent: '#7fb800', accentText: '#10140a' },
    dark: {
      ...DARK_BASE, background: '#121412', surface: '#1a1d1a', surfaceRaised: '#232723', border: '#2e332e',
      accent: '#b5e61d', accentText: '#10140a', value: '#e9f7c4',
    },
    font: 'jost', radius: 14,
  },
  {
    id: 'hell-klar', label: 'Hell und klar', hint: 'Viel Weiß, ruhige Linien', defaultMode: 'light',
    light: { ...LIGHT_BASE, background: '#fafbfc', border: '#e8eaee', value: '#1f6feb' },
    dark: { ...DARK_BASE, value: '#79b8ff' },
    font: 'inter', radius: 16,
  },
  {
    id: 'nacht', label: 'Nacht', hint: 'Sehr dunkel, gedämpft, für Räume ohne Licht', defaultMode: 'dark',
    light: LIGHT_BASE,
    dark: {
      background: '#050608', surface: '#0c0e12', surfaceRaised: '#13161b', text: '#c9ced6',
      textMuted: '#6b7280', border: '#1c2027', accent: '#8b7cf6', accentText: '#0b0a14',
      ok: '#2ea043', warn: '#bb8009', alarm: '#da3633', value: '#e6e9ee',
    },
    font: 'inter', radius: 12,
  },
  {
    id: 'kontrast', label: 'Kontrast', hint: 'Maximal lesbar, auch mit Handschuh im Gegenlicht', defaultMode: 'dark',
    light: { ...LIGHT_BASE, background: '#ffffff', text: '#000000', textMuted: '#2b2b2b', border: '#000000', accent: '#0047ff' },
    dark: {
      background: '#000000', surface: '#0a0a0a', surfaceRaised: '#141414', text: '#ffffff',
      textMuted: '#d0d0d0', border: '#ffffff', accent: '#ffd400', accentText: '#000000',
      ok: '#00e676', warn: '#ffd400', alarm: '#ff1744', value: '#ffffff',
    },
    font: 'roboto-condensed', radius: 6,
  },
];

export const DEFAULT_PRESET_ID = 'hvacloud';

const HEX = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(v: unknown): v is string {
  return typeof v === 'string' && HEX.test(v);
}

export function presetById(id: string | undefined): ThemePreset {
  return THEME_PRESETS.find((p) => p.id === id) ?? THEME_PRESETS.find((p) => p.id === DEFAULT_PRESET_ID)!;
}

export function fontById(id: string | undefined): FontOption {
  return FONT_OPTIONS.find((f) => f.id === id) ?? FONT_OPTIONS.find((f) => f.id === 'inter')!;
}

/** Tolerant reader for the `theme` field of a project body. */
export function parseTheme(raw: unknown): HmiTheme | null {
  if (raw === null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const colors: Partial<ThemeColors> = {};
  if (o.colors && typeof o.colors === 'object') {
    for (const [k, v] of Object.entries(o.colors as Record<string, unknown>)) {
      if (k in LIGHT_BASE && isHexColor(v)) (colors as Record<string, string>)[k] = v.toLowerCase();
    }
  }
  const mode = o.mode === 'light' || o.mode === 'dark' || o.mode === 'auto' ? o.mode : undefined;
  const density = o.density === 'compact' || o.density === 'touch' || o.density === 'comfortable' ? o.density : undefined;
  const radius = typeof o.radius === 'number' && o.radius >= 0 && o.radius <= 48 ? Math.round(o.radius) : undefined;
  return {
    preset: typeof o.preset === 'string' ? o.preset : undefined,
    mode,
    colors,
    font: typeof o.font === 'string' ? o.font : undefined,
    radius,
    density,
  };
}

/** Names of the twelve theme colours, in the order the editor shows them. */
export const THEME_COLOR_KEYS: readonly (keyof ThemeColors)[] = [
  'background', 'surface', 'surfaceRaised', 'border',
  'text', 'textMuted', 'value', 'accent',
  'accentText', 'ok', 'warn', 'alarm',
];

/**
 * A theme without empty parts: no `undefined` fields, no empty colour map,
 * colours only as lower-case #rrggbb. What the editor saves, so the body
 * stays small and two equal themes serialise the same.
 */
export function compactTheme(t: HmiTheme): HmiTheme {
  const out: HmiTheme = {};
  if (t.preset) out.preset = t.preset;
  if (t.mode) out.mode = t.mode;
  const colors: Partial<ThemeColors> = {};
  for (const key of THEME_COLOR_KEYS) {
    const v = t.colors?.[key];
    if (isHexColor(v)) colors[key] = v.toLowerCase();
  }
  if (Object.keys(colors).length > 0) out.colors = colors;
  if (t.font) out.font = t.font;
  if (typeof t.radius === 'number' && Number.isFinite(t.radius)) {
    out.radius = Math.max(0, Math.min(48, Math.round(t.radius)));
  }
  if (t.density) out.density = t.density;
  return out;
}

/** Set one colour, or with `undefined` (or anything but #rrggbb) go back to
 *  the preset's colour. */
export function withThemeColor(t: HmiTheme, key: keyof ThemeColors, value: string | undefined): HmiTheme {
  const colors: Partial<ThemeColors> = { ...(t.colors ?? {}) };
  if (isHexColor(value)) colors[key] = value.toLowerCase();
  else delete colors[key];
  return compactTheme({ ...t, colors });
}

export interface ResolvedTheme {
  presetId: string;
  /** Light or dark, after 'auto' was decided. */
  scheme: 'light' | 'dark';
  colors: ThemeColors;
  font: FontOption;
  radius: number;
  density: ThemeDensity;
}

/**
 * The theme as it renders. `systemDark` is what 'auto' follows: the app
 * theme in the cloud, `prefers-color-scheme` in the standalone runtime.
 */
export function resolveTheme(raw: unknown, systemDark: boolean, forced?: 'light' | 'dark'): ResolvedTheme {
  const t = parseTheme(raw) ?? {};
  const preset = presetById(t.preset);
  const mode = forced ?? t.mode ?? preset.defaultMode;
  const scheme: 'light' | 'dark' = mode === 'auto' ? (systemDark ? 'dark' : 'light') : mode;
  const base = scheme === 'dark' ? preset.dark : preset.light;
  return {
    presetId: preset.id,
    scheme,
    colors: { ...base, ...(t.colors ?? {}) },
    font: fontById(t.font ?? preset.font),
    radius: t.radius ?? preset.radius,
    density: t.density ?? 'comfortable',
  };
}

/** Mix two #rrggbb colours, `amount` of `b` into `a`. */
export function mix(a: string, b: string, amount: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (shift: number) => {
    const ca = (pa >> shift) & 0xff;
    const cb = (pb >> shift) & 0xff;
    return Math.round(ca + (cb - ca) * amount);
  };
  const out = (ch(16) << 16) | (ch(8) << 8) | ch(0);
  return `#${out.toString(16).padStart(6, '0')}`;
}

const DENSITY_SCALE: Record<ThemeDensity, number> = { compact: 0.85, comfortable: 1, touch: 1.2 };

/**
 * CSS custom properties for the frame element. Only values that passed
 * isHexColor and numeric checks get here, so the result is safe to put into
 * a style attribute.
 */
export function themeCssVars(theme: ResolvedTheme): Record<string, string> {
  const c = theme.colors;
  const r = theme.radius;
  return {
    '--surface-base': c.background,
    '--surface-panel': c.surface,
    '--surface-elevated': c.surfaceRaised,
    '--surface-hover': mix(c.surfaceRaised, c.text, 0.06),
    '--border-subtle': c.border,
    '--border-strong': mix(c.border, c.text, 0.18),
    '--text-primary': c.text,
    '--text-secondary': c.textMuted,
    '--text-muted': mix(c.textMuted, c.background, 0.3),
    '--text-on-accent': c.accentText,
    '--accent': c.accent,
    '--accent-hover': mix(c.accent, theme.scheme === 'dark' ? '#ffffff' : '#000000', 0.12),
    '--accent-muted': mix(c.accent, c.background, 0.78),
    '--accent-contrast': c.accentText,
    '--status-ok': c.ok,
    '--status-warn': c.warn,
    '--status-error': c.alarm,
    '--hmi-value': c.value,
    '--radius-1': `${Math.round(r * 0.4)}px`,
    '--radius-2': `${Math.round(r * 0.7)}px`,
    '--radius-3': `${r}px`,
    '--font-sans': theme.font.stack,
    '--hmi-density': String(DENSITY_SCALE[theme.density]),
  };
}
